import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useMedicines } from '../../hooks/useMedicines';
import { format, addDays, startOfWeek, isSameDay, isToday } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCalendar, FiClock, FiCheck, FiX, FiChevronLeft, FiChevronRight } = FiIcons;

const Schedule = () => {
  const { medicines } = useMedicines();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date()));

  // Generate week days
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));

  // Mock schedule data - in real app, this would come from medicine logs
  const generateScheduleForDate = (date) => {
    return medicines.flatMap(medicine => 
      medicine.timing?.map(time => ({
        id: `${medicine.id}-${time}-${format(date, 'yyyy-MM-dd')}`,
        medicineId: medicine.id,
        medicineName: medicine.name,
        dosage: medicine.dosage,
        type: medicine.type,
        time,
        date: format(date, 'yyyy-MM-dd'),
        status: Math.random() > 0.3 ? 'taken' : Math.random() > 0.5 ? 'missed' : 'pending',
        foodInstruction: medicine.food_instruction
      })) || []
    );
  };

  const getTodaySchedule = () => generateScheduleForDate(selectedDate);
  
  const getWeeklyStats = () => {
    let totalScheduled = 0;
    let totalTaken = 0;
    
    weekDays.forEach(day => {
      const daySchedule = generateScheduleForDate(day);
      totalScheduled += daySchedule.length;
      totalTaken += daySchedule.filter(item => item.status === 'taken').length;
    });

    return {
      adherenceRate: totalScheduled > 0 ? Math.round((totalTaken / totalScheduled) * 100) : 0,
      totalScheduled,
      totalTaken,
      totalMissed: totalScheduled - totalTaken
    };
  };

  const stats = getWeeklyStats();

  const getStatusColor = (status) => {
    switch (status) {
      case 'taken': return 'text-green-600 bg-green-50 border-green-200';
      case 'missed': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'taken': return FiCheck;
      case 'missed': return FiX;
      default: return FiClock;
    }
  };

  const navigateWeek = (direction) => {
    setCurrentWeek(prev => addDays(prev, direction * 7));
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Medicine Schedule</h1>
        <p className="text-gray-600 mt-2">View and manage your daily medicine schedule</p>
      </div>

      {/* Weekly Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Adherence Rate</p>
              <p className="text-3xl font-bold">{stats.adherenceRate}%</p>
            </div>
            <SafeIcon icon={FiIcons.FiTrendingUp} className="text-2xl text-blue-100" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Scheduled</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalScheduled}</p>
            </div>
            <SafeIcon icon={FiCalendar} className="text-2xl text-gray-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Taken</p>
              <p className="text-3xl font-bold text-green-600">{stats.totalTaken}</p>
            </div>
            <SafeIcon icon={FiCheck} className="text-2xl text-green-400" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Missed</p>
              <p className="text-3xl font-bold text-red-600">{stats.totalMissed}</p>
            </div>
            <SafeIcon icon={FiX} className="text-2xl text-red-400" />
          </div>
        </motion.div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Week of {format(currentWeek, 'MMM d, yyyy')}
        </h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiChevronLeft} className="text-gray-600" />
          </button>
          <button
            onClick={() => setCurrentWeek(startOfWeek(new Date()))}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Today
          </button>
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiChevronRight} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Week Calendar */}
      <div className="grid grid-cols-7 gap-4 mb-8">
        {weekDays.map((day, index) => {
          const daySchedule = generateScheduleForDate(day);
          const isSelected = isSameDay(day, selectedDate);
          const isDayToday = isToday(day);

          return (
            <motion.button
              key={day.toISOString()}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => setSelectedDate(day)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                isSelected 
                  ? 'border-blue-500 bg-blue-50' 
                  : isDayToday 
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">{format(day, 'EEE')}</p>
                <p className={`text-xl font-bold mb-2 ${
                  isDayToday ? 'text-purple-600' : isSelected ? 'text-blue-600' : 'text-gray-900'
                }`}>
                  {format(day, 'd')}
                </p>
                <div className="space-y-1">
                  {daySchedule.slice(0, 3).map(item => (
                    <div
                      key={item.id}
                      className={`w-full h-2 rounded-full ${
                        item.status === 'taken' ? 'bg-green-400' :
                        item.status === 'missed' ? 'bg-red-400' : 'bg-blue-400'
                      }`}
                    />
                  ))}
                  {daySchedule.length > 3 && (
                    <p className="text-xs text-gray-500">+{daySchedule.length - 3} more</p>
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Selected Day Schedule */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            isToday(selectedDate) ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d')}
          </span>
        </div>

        <div className="space-y-4">
          {getTodaySchedule().length === 0 ? (
            <div className="text-center py-12">
              <SafeIcon icon={FiCalendar} className="text-6xl text-gray-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No medicines scheduled</h4>
              <p className="text-gray-600">No medicines are scheduled for this date.</p>
            </div>
          ) : (
            getTodaySchedule()
              .sort((a, b) => a.time.localeCompare(b.time))
              .map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-xl border-2 ${getStatusColor(item.status)}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(item.status)}`}>
                        <SafeIcon icon={getStatusIcon(item.status)} className="text-lg" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{item.medicineName}</h4>
                        <p className="text-sm text-gray-600">
                          {item.dosage} • {item.type} • {item.foodInstruction?.replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{item.time}</p>
                      <p className="text-sm capitalize text-gray-600">{item.status}</p>
                    </div>
                  </div>
                </motion.div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Schedule;